import type { FC, ReactNode } from 'react'
import type { ThemePreference } from '@/lib/theme'

import { useEffect, useState } from 'react'
import { Monitor, Moon, Sun } from 'lucide-react'
import { cn } from '@/lib/utils'
import { THEME_STORAGE_KEY, isThemePreference } from '@/lib/theme'

interface ThemeToggleProps {
    className?: string
}

const NEXT_THEME: Record<ThemePreference, ThemePreference> = {
    system: 'light',
    light: 'dark',
    dark: 'system'
}

const THEME_LABEL: Record<ThemePreference, string> = {
    system: 'system',
    light: 'light',
    dark: 'dark'
}

const ThemeToggle: FC<ThemeToggleProps> = ({ className }): ReactNode => {
    const [theme, setTheme] = useState<ThemePreference>('system')
    const [ready, setReady] = useState(false)

    useEffect(() => {
        const saved = localStorage.getItem(THEME_STORAGE_KEY)

        if (isThemePreference(saved)) setTheme(saved)
        setReady(true)
    }, [])

    useEffect(() => {
        if (!ready) return

        const media = window.matchMedia('(prefers-color-scheme: dark)')

        const apply = () => {
            const dark = theme === 'dark' || (theme === 'system' && media.matches)
            const root = document.documentElement

            root.classList.toggle('dark', dark)
            root.style.colorScheme = dark ? 'dark' : 'light'
        }

        apply()

        if (theme !== 'system') return

        media.addEventListener('change', apply)

        return () => media.removeEventListener('change', apply)
    }, [theme, ready])

    const handleClick = () => {
        const next = NEXT_THEME[theme]

        setTheme(next)

        try {
            localStorage.setItem(THEME_STORAGE_KEY, next)
        }
        catch {
            // Storage can be unavailable (private mode); the choice still applies for this visit.
        }
    }

    const Icon = theme === 'light' ? Sun : theme === 'dark' ? Moon : Monitor
    const label = `Theme: ${THEME_LABEL[theme]}. Switch to ${THEME_LABEL[NEXT_THEME[theme]]}`

    return (
        <button
            type='button'
            onClick={handleClick}
            aria-label={label}
            title={label}
            className={cn(
                'grid size-10 place-items-center rounded-lg text-foreground/80 transition-colors hover:bg-muted hover:text-foreground',
                className
            )}
        >
            <Icon className='size-5' strokeWidth={1.8} />
        </button>
    )
}

export default ThemeToggle
